import { useState } from 'react';
import { Briefcase } from 'lucide-react';

interface Props {
  data: any;
  onChange: (data: any) => void;
}

export function BusinessDetailsStep({ data, onChange }: Props) {
  const [errors, setErrors] = useState({
    businessName: '',
    yearsInBusiness: '',
    bio: ''
  });
  const [touched, setTouched] = useState({
    businessName: false,
    yearsInBusiness: false,
    bio: false
  });

  const validateField = (field: string, value: any) => {
    let error = '';
    
    switch (field) {
      case 'businessName':
        if (!value?.trim()) error = 'Business name is required';
        break;
      case 'yearsInBusiness':
        if (!value && value !== 0) error = 'Years in business is required';
        else if (value < 0) error = 'Years cannot be negative';
        break;
      case 'bio':
        if (!value?.trim()) error = 'Business description is required';
        else if (value.trim().length < 50) error = 'Description must be at least 50 characters';
        break;
    }
    
    setErrors(prev => ({ ...prev, [field]: error }));
    return error === '';
  };

  const updateField = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
    if (touched[field as keyof typeof touched]) {
      validateField(field, value);
    }
  };

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, data[field]);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Business Details</h2>
        <p className="text-gray-600">Tell us about your business</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Briefcase className="w-4 h-4 inline mr-2" />
            Business Name *
          </label>
          <input
            type="text"
            value={data.businessName || ''}
            onChange={(e) => updateField('businessName', e.target.value)}
            onBlur={() => handleBlur('businessName')}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              errors.businessName && touched.businessName ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.businessName && touched.businessName && (
            <p className="text-xs text-red-600 mt-1">{errors.businessName}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Years in Business *
          </label>
          <input
            type="number"
            value={data.yearsInBusiness || ''}
            onChange={(e) => updateField('yearsInBusiness', parseInt(e.target.value) || 0)}
            onBlur={() => handleBlur('yearsInBusiness')}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              errors.yearsInBusiness && touched.yearsInBusiness ? 'border-red-500' : 'border-gray-300'
            }`}
            min="0"
          />
          {errors.yearsInBusiness && touched.yearsInBusiness && (
            <p className="text-xs text-red-600 mt-1">{errors.yearsInBusiness}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Business Description *
        </label>
        <textarea
          value={data.bio || ''}
          onChange={(e) => updateField('bio', e.target.value)}
          onBlur={() => handleBlur('bio')}
          rows={4}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
            errors.bio && touched.bio ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Tell customers about your business and expertise..."
        />
        <div className="flex justify-between items-center mt-1">
          <div>
            {errors.bio && touched.bio && (
              <p className="text-xs text-red-600">{errors.bio}</p>
            )}
          </div>
          <p className="text-xs text-gray-500">
            {(data.bio || '').length} characters (min 50)
          </p>
        </div>
      </div>
    </div>
  );
}
